import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { useLanguage } from '../../../../context/LanguageContext';
import './product-search-dialog.css';

// Product interface
export interface BudgetProduct {
    id: string;
    productNamePt?: string;
    productNameEn?: string;
    productBrand?: string;
    productImage?: string;
    productCode?: string;
    categoryId: number | string;
}

interface ProductSearchDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectProduct: (product: BudgetProduct) => void;
    selectedProducts: BudgetProduct[];
}

// Category data with translations
interface CategoryData {
    id: number;
    namePt: string;
    nameEn: string;
}

const CATEGORIES: CategoryData[] = [
    { id: 0, namePt: 'Todas as Categorias', nameEn: 'All Categories' },
    { id: 1, namePt: 'Eletricidade', nameEn: 'Electricity' },
    { id: 2, namePt: 'Equipamento Interior', nameEn: 'Interior Equipment' },
    { id: 3, namePt: 'Equipamento Exterior', nameEn: 'Exterior Equipment' },
    { id: 4, namePt: 'Lubrificantes', nameEn: 'Lubricants' },
    { id: 5, namePt: 'Manutenção', nameEn: 'Maintenance' },
    { id: 6, namePt: 'Peças Técnicas', nameEn: 'Technical Parts' },
    { id: 7, namePt: 'Pneus', nameEn: 'Tires' },
    { id: 8, namePt: 'Som, Multimédia e Navegação', nameEn: 'Sound, Multimedia & Navigation' },
    { id: 9, namePt: 'Ferramentas', nameEn: 'Tools' },
];

// Normalize text for fuzzy search
function normalizeSearchText(text: string): string {
    if (!text) return '';
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove accents
        .replace(/[\/\-_.,;:!?'"()[\]{}|\\@#$%^&*+=<>~`]/g, '') // Remove special chars
        .replace(/\s+/g, ' ')
        .trim();
}

const ProductSearchDialog: React.FC<ProductSearchDialogProps> = ({
    isOpen,
    onClose,
    onSelectProduct,
    selectedProducts
}) => {
    const { language } = useLanguage();
    const [searchTerm, setSearchTerm] = useState('');
    const [products, setProducts] = useState<BudgetProduct[]>([]);
    const [filteredProducts, setFilteredProducts] = useState<BudgetProduct[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<number>(0); // 0 = all categories
    const [isMobileCategoryOpen, setIsMobileCategoryOpen] = useState(false);
    const dialogRef = useRef<HTMLDivElement>(null);

    // Load all products from Firestore
    useEffect(() => {
        if (!isOpen) return;

        const loadProducts = async () => {
            setIsLoading(true);
            try {
                const windowAny = window as any;
                const db = windowAny.firebaseDb;
                
                if (!db) {
                    setIsLoading(false);
                    return;
                }

                // Get products collection using FirestorePathService if available
                let productsRef;
                if (windowAny.FirestorePathService) {
                    productsRef = windowAny.FirestorePathService.getCollectionRef(db, 'products');
                } else {
                    productsRef = collection(db, 'products');
                }

                // Load all products (no category filter)
                const snapshot = await getDocs(productsRef);
                
                const loadedProducts: BudgetProduct[] = snapshot.docs
                    .map(doc => {
                        const data = doc.data() as {
                            productNamePt?: string;
                            productNameEn?: string;
                            productBrand?: string;
                            productImages?: string[];
                            productCode?: string;
                            categoryId?: number | string;
                        };
                        return {
                            id: doc.id,
                            productNamePt: data.productNamePt || '',
                            productNameEn: data.productNameEn || '',
                            productBrand: data.productBrand || '',
                            productImage: data.productImages?.[0] || '',
                            productCode: data.productCode || '',
                            categoryId: data.categoryId ?? 0
                        };
                    })
                    // Exclude category 10 (2-wheel mobility - usually hidden)
                    .filter(p => {
                        const catId = typeof p.categoryId === 'string' ? parseInt(p.categoryId, 10) : p.categoryId;
                        return catId !== 10;
                    });

                setProducts(loadedProducts);
                setFilteredProducts(loadedProducts);
            } catch (_) {
            } finally {
                setIsLoading(false);
            }
        };

        loadProducts();
    }, [isOpen]);

    // Reset filters when dialog opens
    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
            setSelectedCategory(0);
        }
    }, [isOpen]);

    // Filter products based on search term and category
    useEffect(() => {
        let filtered = [...products];

        // Apply category filter
        if (selectedCategory !== 0) {
            filtered = filtered.filter(product => {
                const catId = typeof product.categoryId === 'string' 
                    ? parseInt(product.categoryId, 10) 
                    : product.categoryId;
                return catId === selectedCategory;
            });
        }

        // Apply search filter
        if (searchTerm.trim()) {
            const normalizedSearch = normalizeSearchText(searchTerm);
            filtered = filtered.filter(product => {
                const namePt = normalizeSearchText(product.productNamePt || '');
                const nameEn = normalizeSearchText(product.productNameEn || '');
                const brand = normalizeSearchText(product.productBrand || '');
                const code = normalizeSearchText(product.productCode || '');

                return namePt.includes(normalizedSearch) ||
                       nameEn.includes(normalizedSearch) ||
                       brand.includes(normalizedSearch) ||
                       code.includes(normalizedSearch);
            });
        }

        setFilteredProducts(filtered);
    }, [searchTerm, products, selectedCategory]);

    // Get category name based on language
    const getCategoryName = useCallback((category: CategoryData) => {
        return language === 'pt' ? category.namePt : category.nameEn;
    }, [language]);

    // Get count of products in a category
    const getCategoryProductCount = useCallback((categoryId: number) => {
        if (categoryId === 0) return products.length;
        return products.filter(p => {
            const catId = typeof p.categoryId === 'string' 
                ? parseInt(p.categoryId, 10) 
                : p.categoryId;
            return catId === categoryId;
        }).length;
    }, [products]);

    // Handle click outside to close
    const handleBackdropClick = useCallback((e: React.MouseEvent) => {
        if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
            onClose();
        }
    }, [onClose]);

    // Handle product selection (toggle)
    const handleSelectProduct = useCallback((product: BudgetProduct) => {
        onSelectProduct(product);
    }, [onSelectProduct]);

    // Check if product is already selected
    const isProductSelected = useCallback((productId: string) => {
        return selectedProducts.some(p => p.id === productId);
    }, [selectedProducts]);

    // Get product display name based on language
    const getProductName = useCallback((product: BudgetProduct) => {
        return language === 'pt' 
            ? (product.productNamePt || product.productNameEn || 'Product')
            : (product.productNameEn || product.productNamePt || 'Product');
    }, [language]);

    if (!isOpen) return null;

    return (
        <div className="product-search-dialog-overlay" onClick={handleBackdropClick}>
            <div className="product-search-dialog product-search-dialog--with-sidebar" ref={dialogRef}>
                <div className="product-search-dialog__header">
                    <h3>
                        {language === 'pt' ? 'Selecionar Produto' : 'Select Product'}
                    </h3>
                    <button 
                        type="button" 
                        className="product-search-dialog__close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="product-search-dialog__body">
                    {/* Category Sidebar - Left */}
                    <div className={`product-search-dialog__sidebar ${isMobileCategoryOpen ? 'open' : ''}`}>
                        <div className="product-search-dialog__sidebar-header">
                            <h4>{language === 'pt' ? 'Categorias' : 'Categories'}</h4>
                            <button 
                                className="product-search-dialog__sidebar-close"
                                onClick={() => setIsMobileCategoryOpen(false)}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <ul className="product-search-dialog__category-list">
                            {CATEGORIES.map(category => (
                                <li 
                                    key={category.id}
                                    className={`product-search-dialog__category-item ${selectedCategory === category.id ? 'active' : ''}`}
                                    onClick={() => {
                                        setSelectedCategory(category.id);
                                        setIsMobileCategoryOpen(false);
                                    }}
                                >
                                    <span className="product-search-dialog__category-name">
                                        {getCategoryName(category)}
                                    </span>
                                    <span className="product-search-dialog__category-count">
                                        {getCategoryProductCount(category.id)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Main Content - Right */}
                    <div className="product-search-dialog__main">
                        <div className="product-search-dialog__search">
                            <button 
                                className="product-search-dialog__filter-toggle"
                                onClick={() => setIsMobileCategoryOpen(true)}
                            >
                                <i className="fas fa-filter"></i>
                            </button>
                            <input
                                type="text"
                                placeholder={language === 'pt' ? 'Pesquisar produtos...' : 'Search products...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                autoFocus
                            />
                            <i className="fas fa-search product-search-dialog__search-icon"></i>
                        </div>

                        {/* Active filter indicator */}
                        {selectedCategory !== 0 && (
                            <div className="product-search-dialog__active-filter">
                                <span>
                                    {language === 'pt' ? 'Filtro: ' : 'Filter: '}
                                    <strong>{getCategoryName(CATEGORIES.find(c => c.id === selectedCategory)!)}</strong>
                                </span>
                                <button onClick={() => setSelectedCategory(0)}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        )}

                        <div className="product-search-dialog__content">
                            {isLoading ? (
                                <div className="product-search-dialog__loading">
                                    <i className="fas fa-spinner fa-spin"></i>
                                    <span>{language === 'pt' ? 'A carregar produtos...' : 'Loading products...'}</span>
                                </div>
                            ) : filteredProducts.length === 0 ? (
                                <div className="product-search-dialog__empty">
                                    <i className="fas fa-box-open"></i>
                                    <span>
                                        {searchTerm || selectedCategory !== 0
                                            ? (language === 'pt' ? 'Nenhum produto encontrado' : 'No products found')
                                            : (language === 'pt' ? 'Sem produtos disponíveis' : 'No products available')
                                        }
                                    </span>
                                </div>
                            ) : (
                                <div className="product-search-dialog__grid">
                                    {filteredProducts.map(product => (
                                        <div 
                                            key={product.id}
                                            className={`product-search-dialog__item ${isProductSelected(product.id) ? 'selected' : ''}`}
                                            onClick={() => handleSelectProduct(product)}
                                        >
                                            <div className="product-search-dialog__item-image">
                                                {product.productImage ? (
                                                    <img loading="lazy"
                                                        src={product.productImage} 
                                                        alt={getProductName(product)}
                                                    />
                                                ) : (
                                                    <div className="product-search-dialog__item-placeholder">
                                                        <i className="fas fa-image"></i>
                                                    </div>
                                                )}
                                                {isProductSelected(product.id) && (
                                                    <div className="product-search-dialog__item-selected-badge">
                                                        <i className="fas fa-check"></i>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="product-search-dialog__item-info">
                                                <h4>{getProductName(product)}</h4>
                                                {product.productBrand && (
                                                    <span className="product-search-dialog__item-brand">
                                                        {product.productBrand}
                                                    </span>
                                                )}
                                                {product.productCode && (
                                                    <span className="product-search-dialog__item-code">
                                                        REF: {product.productCode}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="product-search-dialog__footer">
                    <span className="product-search-dialog__count">
                        {language === 'pt' 
                            ? `${selectedProducts.length} produto(s) selecionado(s)`
                            : `${selectedProducts.length} product(s) selected`
                        }
                    </span>
                    <button 
                        type="button"
                        className="thm-btn"
                        onClick={onClose}
                    >
                        {language === 'pt' ? 'Concluir' : 'Done'}
                        <span><i className="icon-next"></i></span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProductSearchDialog;
